import { getSupabase } from './supabaseClient'
import { panelToken } from './panelToken'

/**
 * Proving who is asking, for every call to the API Worker
 * -------------------------------------------------------------
 * Two surfaces, two kinds of proof, two different headers — and the headers
 * differ on purpose. Sending one token and letting the Worker try each verifier
 * in turn would mean a token rejected by the first gets a second chance at the
 * second, and "which check finally passed" is not a question an authentication
 * path should leave open.
 *
 * This lived inside miroQueue while the queue was the only thing that needed
 * it. Chat needs it too, so it moved here rather than being copied — the same
 * mistake three copies of the panel token helper had already cost us an
 * evening.
 */

/** Where the backend lives. Empty means same origin. */
export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''

/** Which surface is asking. Not an identity — only which proof to attach. */
export type ApiCaller = 'web' | 'panel'

/** The website's proof: the Supabase session, issued after Microsoft signed them in. */
export async function webToken(): Promise<string> {
  const supabase = getSupabase()
  if (!supabase) throw new Error('Not connected to the database')
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new Error('Sign in first')
  return token
}

/**
 * Which surface this bundle is. Declared once at the entry point rather than
 * sniffed at each call — the same reasoning as setAssetRepository: which
 * surface this is belongs to the entry point, not to any screen.
 */
let surface: ApiCaller = 'web'

export function setApiSurface(next: ApiCaller): void {
  surface = next
}

export function apiSurface(): ApiCaller {
  return surface
}

export async function authHeaders(caller: ApiCaller = surface): Promise<Record<string, string>> {
  return caller === 'web'
    ? { Authorization: `Bearer ${await webToken()}` }
    : { 'X-WW-Miro-Token': await panelToken() }
}
